// src/contexts/NotificationContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  Notification, 
  NotificationFilter, 
  NotificationStats, 
  NotificationSettings,
  NotificationType 
} from '../types/notifications';
import { notificationService } from '../services/notificationService';

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  stats: NotificationStats;
  settings: NotificationSettings;
  
  // Actions
  markAsRead: (id: string) => void;
  markAsUnread: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearAllNotifications: () => void;
  getFilteredNotifications: (filter?: NotificationFilter) => Notification[];
  updateSettings: (settings: Partial<NotificationSettings>) => void;
  
  // Helper methods
  notifyAssignment: (requestId: string, projectName: string, assignedTo: string) => void;
  notifyTaskCompleted: (taskId: string, taskTitle: string, completedBy: string) => void;
  notifyDeadlineAlert: (requestId: string, projectName: string, daysLeft: number) => void;
  notifyStatusUpdate: (requestId: string, projectName: string, newStatus: string) => void;
  notifySystem: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const DEFAULT_SETTINGS: NotificationSettings = {
  enableToasts: true,
  enableSounds: false,
  autoHideDelay: 5000,
  maxToasts: 5,
  enabledTypes: Object.values(NotificationType)
};

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);

  // Subscribe to notification service updates
  useEffect(() => {
    const unsubscribe = notificationService.subscribe((updatedNotifications) => {
      setNotifications(updatedNotifications);
    });

    // Load initial notifications
    setNotifications(notificationService.getNotifications());

    // Load settings from localStorage
    const savedSettings = localStorage.getItem('ufficio-stile-notification-settings');
    if (savedSettings) {
      try {
        setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(savedSettings) });
      } catch (error) {
        console.warn('Failed to load notification settings:', error);
      }
    }

    return unsubscribe;
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('ufficio-stile-notification-settings', JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save notification settings:', error);
    }
  }, [settings]);

  const markAsRead = (id: string) => {
    notificationService.markAsRead(id);
  };

  const markAsUnread = (id: string) => {
    notificationService.markAsUnread(id);
  };

  const markAllAsRead = () => {
    notificationService.markAllAsRead();
  };

  const deleteNotification = (id: string) => {
    notificationService.deleteNotification(id);
  };

  const clearAllNotifications = () => {
    notificationService.clearAllNotifications();
  };

  const getFilteredNotifications = (filter?: NotificationFilter) => {
    return notificationService.getNotifications(filter);
  };

  const updateSettings = (newSettings: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Helper notification methods
  const notifyAssignment = (requestId: string, projectName: string, assignedTo: string) => {
    notificationService.notifyAssignment(requestId, projectName, assignedTo);
  };

  const notifyTaskCompleted = (taskId: string, taskTitle: string, completedBy: string) => {
    notificationService.notifyTaskCompleted(taskId, taskTitle, completedBy);
  };

  const notifyDeadlineAlert = (requestId: string, projectName: string, daysLeft: number) => {
    notificationService.notifyDeadlineAlert(requestId, projectName, daysLeft);
  };

  const notifyStatusUpdate = (requestId: string, projectName: string, newStatus: string) => {
    notificationService.notifyStatusUpdate(requestId, projectName, newStatus);
  };

  const notifySystem = (message: string) => {
    notificationService.notifySystem(message);
  };

  const unreadCount = notificationService.getUnreadCount();
  const stats = notificationService.getStats();

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    stats,
    settings,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    getFilteredNotifications,
    updateSettings,
    notifyAssignment,
    notifyTaskCompleted,
    notifyDeadlineAlert,
    notifyStatusUpdate,
    notifySystem,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}